import { OrderExecutionTermsOperationKind } from 'api/types/entity/account/order';
import { toZeroTime } from 'helpers';
import {
  number,
  object,
  string,
  selectSchema,
  date,
  array,
  localFileSchema,
} from 'libs/form';
import { TFunc } from 'libs/i18n';

import { isRequiredAdditionalParameters } from '../../../helpers';
import { OrderFormType } from '../type';

import { getDetailsSchema } from './common/detailsSchema';
import { getFinancialInstrumentSchema } from './common/financialInstrumentSchema';

export const MAX_FILES = 10;

export const getSecuritiesWithdrawalSchema = (t: TFunc) =>
  object({
    executionTerms: object({
      additionalNotes: string().nullable().max(200),
      operationKind: selectSchema(
        Object.values(OrderExecutionTermsOperationKind)
      )
        .required()
        .default(null),
    }),

    financialInstrument: getFinancialInstrumentSchema(t),

    transferVolume: object({
      quantity: number().required().min(1),
      nominalQuantity: string(),
      unitPrice: string().when((_, schema, opt) => {
        const context = opt.context as OrderFormType;
        if (
          context.executionTerms?.operationKind?.value ===
            OrderExecutionTermsOperationKind.DeliveryFreeOfPayment ||
          context.executionTerms?.operationKind?.value ===
            OrderExecutionTermsOperationKind.DeliveryVersusPayment
        ) {
          return schema.required();
        }
        return schema;
      }),
      currency: object({
        value: string().required(),
        label: string().required(),
      })
        .when((_, schema, opt) => {
          const context = opt.context as OrderFormType;
          if (
            context.executionTerms?.operationKind?.value ===
              OrderExecutionTermsOperationKind.DeliveryFreeOfPayment ||
            context.executionTerms?.operationKind?.value ===
              OrderExecutionTermsOperationKind.DeliveryVersusPayment
          ) {
            return schema.required();
          }
          return schema.nullable();
        })
        .default(null),
      transactionAmount: string().when((_, schema, opt) => {
        const context = opt.context as OrderFormType;
        if (
          context.executionTerms?.operationKind?.value ===
            OrderExecutionTermsOperationKind.DeliveryFreeOfPayment ||
          context.executionTerms?.operationKind?.value ===
            OrderExecutionTermsOperationKind.DeliveryVersusPayment
        ) {
          return schema.required();
        }
        return schema;
      }),
    }),

    confirmationDocument: object({
      contractNumber: string()
        .when((_, schema, opt) => {
          const context = opt.context as OrderFormType;
          if (
            context.executionTerms?.operationKind?.value ===
              OrderExecutionTermsOperationKind.DeliveryFreeOfPayment ||
            context.executionTerms?.operationKind?.value ===
              OrderExecutionTermsOperationKind.DeliveryVersusPayment
          ) {
            return schema.required();
          }
          return schema;
        })
        .max(20),
      contractDate: date().when((_, schema, opt) => {
        const context = opt.context as OrderFormType;
        if (
          context.executionTerms?.operationKind?.value ===
            OrderExecutionTermsOperationKind.DeliveryFreeOfPayment ||
          context.executionTerms?.operationKind?.value ===
            OrderExecutionTermsOperationKind.DeliveryVersusPayment
        ) {
          return schema.required().max(new Date(), t('validation.maxDate'));
        }
        return schema.nullable();
      }),

      files: array()
        .of(localFileSchema.required())
        .min(1)
        .max(MAX_FILES)
        .required(),
    }),

    senderDetails: getDetailsSchema(t),

    recipientDetails: getDetailsSchema(t),

    additionalParameters: object({
      transactionDate: date()
        .nullable()
        .max(new Date(), t('validation.maxDate'))
        .when((_, schema, opt) => {
          const context = opt.context as OrderFormType;
          if (isRequiredAdditionalParameters(context)) {
            return schema.required();
          }
          return schema.nullable();
        }),

      settlementDate: date()
        .nullable()
        .min(toZeroTime(new Date()), t('validation.minDate'))
        .when((_, schema, opt) => {
          const context = opt.context as OrderFormType;
          if (isRequiredAdditionalParameters(context)) {
            return schema.required();
          }
          return schema.nullable();
        }),
    }),
  });
